import { Instagram, Heart, Star, ArrowUpRight, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { LOGO_LIGHT, SOCIALS, NAV, EMERGENT_VOTE, PRIVACY_POLICY } from "../../lib/landing";

const XIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-brand-dark text-brand-inverse pt-20 pb-10"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-8">
        <div className="mb-16 flex flex-col md:flex-row md:items-center justify-between gap-6 rounded-3xl border border-white/10 bg-brand-primary/15 p-7 sm:p-9">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-primary text-brand-inverse">
              <Star size={22} />
            </div>
            <div>
              <p className="font-display text-2xl sm:text-3xl text-brand-inverse tracking-tight">
                Sostieni Safe(H)er
              </p>
              <p className="mt-1.5 text-brand-inverse/60 max-w-md">
                Vota il progetto su Emergent e aiutaci a proteggere più persone.
              </p>
            </div>
          </div>
          <a
            href={EMERGENT_VOTE}
            target="_blank"
            rel="noreferrer"
            data-testid="vote-emergent-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-inverse text-brand-dark px-6 py-3.5 shrink-0 hover:bg-brand-primary hover:text-brand-inverse transition-colors duration-300 active:scale-95"
          >
            Vota su Emergent
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_LIGHT} alt="Safe(H)er" className="h-9 w-9 object-contain" />
              <span className="font-display text-2xl tracking-tight">
                Safe<span className="italic text-brand-primary">(H)</span>er
              </span>
            </div>
            <p className="mt-6 max-w-md text-brand-inverse/60 leading-relaxed font-display text-2xl sm:text-3xl italic">
              Perché ogni donna merita di sentirsi al sicuro.
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-inverse/40">
              Naviga
            </p>
            <ul className="mt-5 space-y-3">
              {NAV.map((n) => (
                <li key={n.href}>
                  <a
                    href={n.href}
                    className="text-brand-inverse/70 hover:text-brand-inverse transition-colors"
                    data-testid={`footer-nav-${n.label.toLowerCase()}`}
                  >
                    {n.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={PRIVACY_POLICY}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-inverse/70 hover:text-brand-inverse transition-colors"
                  data-testid="footer-privacy-policy"
                >
                  Privacy Policy
                </a>
              </li>
              <li>
                <Link
                  to="/delete-account"
                  className="text-brand-inverse/70 hover:text-brand-inverse transition-colors"
                  data-testid="footer-delete-account"
                >
                  Delete my account & data
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-brand-inverse/40">
              Seguici
            </p>
            <div className="mt-5 flex gap-3">
              <a
                href={SOCIALS.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 hover:bg-brand-primary hover:border-brand-primary transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href={SOCIALS.x}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-x"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 hover:bg-brand-primary hover:border-brand-primary transition-colors"
                aria-label="X"
              >
                <XIcon size={16} />
              </a>
            </div>

            <p className="mt-8 text-xs uppercase tracking-[0.2em] text-brand-inverse/40">
              Contattaci
            </p>
            <a
              href="mailto:safherofficial@gmail.com"
              data-testid="footer-email"
              className="mt-4 inline-flex items-center gap-2.5 text-brand-inverse/70 hover:text-brand-inverse transition-colors break-all"
            >
              <Mail size={16} className="shrink-0 text-brand-primary" />
              safherofficial@gmail.com
            </a>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-brand-inverse/50">
          <p className="flex items-center gap-3">
            © {new Date().getFullYear()} Safe(H)er.
            <a
              href={PRIVACY_POLICY}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-privacy-policy-bottom"
              className="underline underline-offset-4 hover:text-brand-inverse transition-colors"
            >
              Privacy Policy
            </a>
            <Link
              to="/delete-account"
              data-testid="footer-delete-account-bottom"
              className="underline underline-offset-4 hover:text-brand-inverse transition-colors"
            >
              Delete my account
            </Link>
          </p>
          <p className="flex items-center gap-1.5">
            Fatto con <Heart size={14} className="text-brand-primary fill-brand-primary" /> per la sicurezza delle donne
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
